import { useEffect, useState, useContext } from 'react';
import { useDatabase } from './useDatabase';
import { WorkspaceContext } from '../context/WorkspaceContext';

export interface TeamDetails {
  id: string;
  name: string;
  memberIds: string[];
  subTeamIds: string[];
}

/**
 * Hook to access team data
 */
export function useTeam(teamId: string | undefined) {
  const { database, loading: databaseLoading } = useDatabase();
  const { safeData } = useContext(WorkspaceContext);
  const [team, setTeam] = useState<TeamDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!database || !teamId || !safeData) {
      if (!database) setLoading(false);
      return;
    }

    try {
      const teams = database.teams();
      const teamObj = teams[teamId];

      if (!teamObj) {
        setError(`Team with ID ${teamId} not found`);
        setTeam(null);
        setLoading(false);
      } else {
        // Fetch team data asynchronously
        const fetchTeamData = async () => {
          try {
            // Get team members
            const memberIds = await teamObj.member_person_ids(safeData, false);
            // Get direct subteams (new API)
            const subTeamIds = await teamObj.member_team_ids(safeData);
            
            // Set team with all data
            setTeam({
              id: teamId,
              name: teamObj.name(),
              memberIds,
              subTeamIds,
            });
            setError(null);
          } catch (err) {
            console.error('Error fetching team data:', err);
            // Still set the team with basic data
            setTeam({
              id: teamId,
              name: teamObj.name(),
              memberIds: [],
              subTeamIds: [],
            });
            setError(null);
          } finally {
            setLoading(false);
          }
        };
        
        fetchTeamData();
      }
    } catch (err) {
      console.error('Error fetching team:', err);
      setError(`Error fetching team: ${err}`);
      setTeam(null);
      setLoading(false);
    }
  }, [database, teamId, safeData]);

  return { team, loading: loading || databaseLoading, error };
}
